<template>
  <Teleport to="body">
    <Transition name="popup-backdrop">
      <div
        v-if="visible"
        class="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        @click="onConfirm"
      />
    </Transition>

    <Transition name="popup-panel">
      <div
        v-if="visible"
        class="fixed inset-0 z-[10001] flex items-center justify-center p-2"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="messageId"
        @keydown.escape.prevent="onConfirm"
      >
        <div class="relative w-full max-w-sm">
          <div
            class="relative rounded-2xl shadow-2xl flex flex-col w-full overflow-hidden bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700/60"
          >
            <div class="h-1.5 w-full" :class="visual.accentBarClass" />

            <button
              type="button"
              class="absolute right-4 top-4 z-10 p-1.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
              aria-label="Close dialog"
              @click="onConfirm"
            >
              <X class="h-4 w-4" stroke-width="2" aria-hidden="true" />
            </button>

            <div class="flex flex-col items-center text-center px-6 pt-5 pb-4">
              <div
                class="mb-4 flex items-center justify-center w-16 h-16 rounded-full ring-4 transition-all"
                :class="visual.iconRingClass"
              >
                <component
                  :is="visual.icon"
                  class="h-8 w-8"
                  :class="visual.iconClass"
                  stroke-width="1.75"
                  aria-hidden="true"
                />
              </div>

              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3"
                :class="visual.badgeClass"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full animate-pulse"
                  :class="visual.badgeDotClass"
                />
                {{ visual.title }}
              </span>

              <p
                :id="messageId"
                class="text-gray-600 dark:text-gray-400 text-sm font-normal leading-relaxed max-w-xs"
              >
                {{ message }}
              </p>
            </div>

            <div class="mx-8 border-t border-gray-100 dark:border-gray-700/60" />

            <div class="mx-8 border-t border-gray-100 dark:border-gray-700/60" />

            <div class="flex items-center justify-center gap-3 px-8 py-4">
              <button
                ref="okButtonRef"
                type="button"
                class="py-2.5 px-8 rounded-xl text-sm font-semibold text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 active:scale-95 shadow-lg flex items-center gap-2"
                :class="visual.buttonClass"
                @click="onConfirm"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { AlertCircle, Info, ServerCrash, ShieldAlert, WifiOff, X } from 'lucide-vue-next'

const props = defineProps<{
  message: string
  route?: string
  status?: string
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'confirm'): void
}>()

const router = useRouter()
const okButtonRef = ref<HTMLButtonElement | null>(null)
const titleId = `error-popup-title-${Math.random().toString(36).slice(2, 9)}`
const messageId = `error-popup-message-${Math.random().toString(36).slice(2, 9)}`

interface PopupVisual {
  icon: Component
  title: string
  iconClass: string
  iconRingClass: string
  accentBarClass: string
  badgeClass: string
  badgeDotClass: string
  buttonClass: string
  codeTagClass: string
}

const visual = computed<PopupVisual>(() => {
  const status = props.status?.toLowerCase() ?? ''

  if (status === '401' || status === '403' || status === 'auth') {
    return {
      icon: ShieldAlert,
      title: 'Session Expired',
      iconClass: 'text-amber-500 dark:text-amber-400',
      iconRingClass: 'bg-amber-50 dark:bg-amber-900/20 ring-amber-50 dark:ring-amber-900/10',
      accentBarClass: 'bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-500',
      badgeClass: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
      badgeDotClass: 'bg-amber-500',
      buttonClass:
        'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-amber-500/30 hover:shadow-amber-500/50 focus-visible:ring-amber-400',
      codeTagClass: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
    }
  }

  if (status === '500' || status === '503' || status === 'server') {
    return {
      icon: ServerCrash,
      title: 'Server Error',
      iconClass: 'text-red-500 dark:text-red-400',
      iconRingClass: 'bg-red-50 dark:bg-red-900/20 ring-red-50 dark:ring-red-900/10',
      accentBarClass: 'bg-gradient-to-r from-red-400 via-red-500 to-rose-600',
      badgeClass: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
      badgeDotClass: 'bg-red-500',
      buttonClass:
        'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-red-500/30 hover:shadow-red-500/50 focus-visible:ring-red-400',
      codeTagClass: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
    }
  }

  if (status === 'network') {
    return {
      icon: WifiOff,
      title: 'Connection Issue',
      iconClass: 'text-orange-500 dark:text-orange-400',
      iconRingClass: 'bg-orange-50 dark:bg-orange-900/20 ring-orange-50 dark:ring-orange-900/10',
      accentBarClass: 'bg-gradient-to-r from-orange-400 via-orange-500 to-amber-500',
      badgeClass: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
      badgeDotClass: 'bg-orange-500',
      buttonClass:
        'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-orange-500/30 hover:shadow-orange-500/50 focus-visible:ring-orange-400',
      codeTagClass: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
    }
  }

  if (status === 'runtime' || status === 'unhandled-rejection') {
    return {
      icon: AlertCircle,
      title: 'Something Went Wrong',
      iconClass: 'text-violet-500 dark:text-violet-400',
      iconRingClass: 'bg-violet-50 dark:bg-violet-900/20 ring-violet-50 dark:ring-violet-900/10',
      accentBarClass: 'bg-gradient-to-r from-violet-400 via-purple-500 to-indigo-500',
      badgeClass: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400',
      badgeDotClass: 'bg-violet-500',
      buttonClass:
        'bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 shadow-violet-500/30 hover:shadow-violet-500/50 focus-visible:ring-violet-400',
      codeTagClass: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
    }
  }

  return {
    icon: Info,
    title: 'Notice',
    iconClass: 'text-blue-500 dark:text-blue-400',
    iconRingClass: 'bg-blue-50 dark:bg-blue-900/20 ring-blue-50 dark:ring-blue-900/10',
    accentBarClass: 'bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-600',
    badgeClass: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    badgeDotClass: 'bg-blue-500',
    buttonClass:
      'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-blue-500/30 hover:shadow-blue-500/50 focus-visible:ring-blue-400',
    codeTagClass: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
  }
})

watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      requestAnimationFrame(() => okButtonRef.value?.focus())
    }
  }
)

function onConfirm(): void {
  emit('confirm')

  if (props.route) {
    if (props.route.startsWith('http://') || props.route.startsWith('https://')) {
      window.location.href = props.route
      return
    }
    void router.push(props.route)
  }
}
</script>

<style scoped>
.popup-backdrop-enter-active,
.popup-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.popup-backdrop-enter-from,
.popup-backdrop-leave-to {
  opacity: 0;
}

.popup-panel-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.popup-panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.popup-panel-enter-from,
.popup-panel-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}
</style>
